import React from 'react';
import { SearchOutlined } from '@mui/icons-material';
import { useL10n } from '@uplift/hooks';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { CPTCode } from '@uplift/api';
import { ProviderMedicalRole } from '@uplift/types';
import { useSessionSuggestedCPTCodesQuery, useProviderCptCodesQuery } from '@uplift/queries';
import { Autocomplete, AutocompleteMulti, getRenderTagsForAutocompleteMulti, groupBy } from '@uplift/ui-v2';
import { TipsAndUpdatesOutlinedIcon } from '@uplift/icons';

const useStyles = makeStyles()(theme => ({
  suggestedIcon: {
    backgroundColor: theme.palette.info.main,
    color: theme.palette.common.white,

    borderRadius: '50%',
    padding: '4px',
    marginRight: theme.spacing(1),
  }
}));

type BaseProviderCptCodeAutocompleteProps = {
  name: string;
  label?: React.ReactNode;
  medicalRole: ProviderMedicalRole;
  multiple?: boolean;
  sessionId?: string;
};

const getCPTCodeLabel = (cptCode?: CPTCode) => `${cptCode?.code}: ${cptCode?.name}`;

// hash for optimization
export const mostCommonCPTCodesForMedicalRoleHash: Record<ProviderMedicalRole, Record<string, number>> = {
  [ProviderMedicalRole.therapist]: {
    '90837': 1,
    '90834': 2,
    '90832': 3,
    '90847': 4,
    '90791': 5,
  },
  [ProviderMedicalRole.psychiatrist]: {
    '99213': 1,
    '99214': 2,
    '90792': 3,
    '99204': 4,
    '90833': 5,
    '90836': 6,
  }
};

export const groupCptCodes = (cptCodes: CPTCode[], mostCommon: Record<string, number> = {}, suggestions: Record<string, unknown> = {}) => {
  const grouped = groupBy(cptCodes, (cptCode) => {
    if (cptCode.code in suggestions) {
      return 'suggested';
    }

    if (cptCode.code in mostCommon) {
      return 'mostCommon';
    }

    return 'other';
  });

  grouped.mostCommon = grouped.mostCommon?.sort((a, b) => {
    return mostCommon[a.code] - mostCommon[b.code];
  });

  return grouped;
};


export type RawProviderCptCodeAutocompleteProps = BaseProviderCptCodeAutocompleteProps & {
  cptCodes: CPTCode[];
  ungroupCptCodes?: boolean;
};

export const RawProviderCptCodeAutocomplete: React.VFC<RawProviderCptCodeAutocompleteProps> = ({ name, cptCodes, medicalRole, label, multiple, sessionId, ungroupCptCodes = false }) => {
  const { classes } = useStyles();
  const l10n = useL10n();
  const mostCommonCPTCodesHash = mostCommonCPTCodesForMedicalRoleHash[medicalRole];
  const { data: cptSuggestions = {} } = useSessionSuggestedCPTCodesQuery(sessionId);

  const cptCodeOptions = React.useMemo(() => {
    const grouped = groupCptCodes(cptCodes, mostCommonCPTCodesHash, cptSuggestions);

    return [
      ...(grouped.suggested ?? []),
      ...(grouped.mostCommon ?? []),
      ...(grouped.other ?? [])
    ];
  }, [ cptCodes, mostCommonCPTCodesHash, cptSuggestions ]);

  const getGroupKey = (option: CPTCode) => {
    if (option.code in cptSuggestions) {
      return l10n.clinicalNotes.suggestedCptCodes;
    }

    if (option.code in mostCommonCPTCodesHash) {
      return l10n.clinicalNotes.mostCommonCptCodes;
    }

    return l10n.clinicalNotes.otherCptCodes;
  };

  if (multiple) {
    return (
      <AutocompleteMulti
        disableCloseOnSelect
        fullWidth
        label={label ?? l10n.clinicalNotes.cptCode}
        name={name}
        extractId
        getOptionLabel={getCPTCodeLabel}
        options={ungroupCptCodes ? cptCodes : cptCodeOptions}
        groupBy={ungroupCptCodes ? undefined : getGroupKey}
        renderTags={getRenderTagsForAutocompleteMulti<CPTCode>(getCPTCodeLabel)}
        processValueChange={(oldV, newV) => {
          const newItems = newV.filter(n => !oldV.includes(n));

          if (newItems) {
            const newPrimary = newItems.find(v => v.codingType === 'primary');

            if (newPrimary) {
              return [ ...newV.filter(v => v.codingType !== 'primary'), newPrimary ];
            }
          }

          return newV;
        }}
      />
    );
  }

  return (
    <Autocomplete
      fullWidth
      disableClearable
      label={label ?? l10n.clinicalNotes.cptCode}
      name={name}
      getOptionLabel={getCPTCodeLabel}
      transformValue={(v) => v?.id}
      options={ungroupCptCodes ? cptCodes : cptCodeOptions}
      groupBy={ungroupCptCodes ? undefined : getGroupKey}
      startAdornment={(v) => {
        if (cptSuggestions[v?.code]) {
          return <TipsAndUpdatesOutlinedIcon fontSize="small" className={classes.suggestedIcon} />;
        }
        return <SearchOutlined />;
      }}
      renderOption={opt => {
        if (cptSuggestions[opt.code]) {
          return (
            <Box display="flex" alignItems="center">
              <TipsAndUpdatesOutlinedIcon fontSize="small" className={classes.suggestedIcon} />
              {getCPTCodeLabel(opt)}
            </Box>
          );
        }

        return getCPTCodeLabel(opt);
      }}
    />
  );
};


export type ProviderCptCodeAutocompleteProps = BaseProviderCptCodeAutocompleteProps & {
  providerId: string;
  filterOutCptCodes?: string[];
  ungroupCptCodes?: boolean;
};


export const ProviderPrimaryCptCodeAutocomplete: React.VFC<ProviderCptCodeAutocompleteProps> = (props) => {
  const { data: { cptCodes = [] } = {} } = useProviderCptCodesQuery(props.providerId);

  const filteredCptCodes = React.useMemo(() => {
    if (props.filterOutCptCodes?.length) {
      return cptCodes.filter(cpt => !props.filterOutCptCodes?.includes(cpt.code));
    }
    return cptCodes;
  }, [ cptCodes, props.filterOutCptCodes ]);


  const primaryCptCodes = React.useMemo(() => {
    return filteredCptCodes.filter(c => c.codingType === 'primary');
  }, [ filteredCptCodes ]);

  return (
    <RawProviderCptCodeAutocomplete cptCodes={primaryCptCodes} {...props} />
  );
};
